import { useEffect } from 'react';
import { IS_EMBED } from '@/lib/isEmbed.ts';
import checkoutStyles from './CheckoutPage.module.css';

interface ContentModalProps {
  /** Whether the modal is currently visible. */
  isOpen: boolean;
  /** Called when the guest dismisses the modal (Escape, backdrop, close X). */
  onClose: () => void;
  /** Header text rendered at the top of the panel and as the ARIA label. */
  title: string;
  /**
   * Body text. Rendered as one `<p>` per non-empty line, so long legal blobs
   * (Terms of Use, Privacy Policy) can be pasted verbatim from `legalContent.ts`
   * without needing JSX-per-paragraph.
   */
  content?: string;
}

/**
 * Reusable scrollable overlay modal for legal text (Terms, Privacy Policy,
 * venue T&Cs). Lifted out of `CheckoutPage.tsx` so `DetailsPage.tsx` can open
 * the same modals when the guest taps the "By clicking Continue…" links, so
 * both surfaces render one legal source of truth. In embed mode the overlay
 * owns scroll-lock; on the standalone site the body scroll-lock protects the
 * host page from double-scroll while the modal is open.
 *
 * @example
 * <ContentModal
 *   isOpen={termsOpen}
 *   onClose={() => setTermsOpen(false)}
 *   title="Terms of Use"
 *   content={TERMS_OF_USE}
 * />
 */
export function ContentModal({ isOpen, onClose, title, content }: ContentModalProps) {
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    if (!IS_EMBED) document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      if (!IS_EMBED) document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    // `role="dialog"` is used here (instead of `<dialog>`) to keep parity with
    // the modal semantics CheckoutPage shipped with: React drives open/close
    // via `isOpen`, backdrop click + Escape both close, and no browser-native
    // top-layer behaviour that would fight the widget's embed z-index stack.
    // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role
    <div className={checkoutStyles.modalOverlay} role="dialog" aria-modal="true" aria-label={title}>
      <div className={checkoutStyles.modalBackdrop} onClick={onClose} aria-hidden="true" />
      <div className={checkoutStyles.modalPanel}>
        <div className={checkoutStyles.modalHeader}>
          <h2 className={checkoutStyles.modalTitle}>{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className={checkoutStyles.modalClose}
            aria-label="Close"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                d="M1 1L13 13M13 1L1 13"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>
        <div className={checkoutStyles.modalContent}>
          {content
            ? content
                .split('\n')
                .filter(Boolean)
                .map((paragraph, i) => <p key={i}>{paragraph}</p>)
            : null}
        </div>
      </div>
    </div>
  );
}
